import type { SseTransferMessage, TransferPlanItem } from "@/api/aiChat";

// ════════════════════════════════════════════════════
//  接送服务卡片（经济线/品质线方案选择）
// ════════════════════════════════════════════════════

/** 方案类型样式 */
export const TRANSFER_TYPE_STYLE: Record<string, {
  bg: string; label: string; icon: string;
  tagBg: string; tagColor: string; accent: string;
}> = {
  economy: {
    bg: "linear-gradient(135deg, #EFF6FF, #DBEAFE)",
    label: "经济优选", icon: "ri-money-cny-circle-line",
    tagBg: "#3B82F6", tagColor: "#fff", accent: "#2563EB",
  },
  premium: {
    bg: "linear-gradient(135deg, #FFF7ED, #FED7AA)",
    label: "尊享品质", icon: "ri-vip-diamond-line",
    tagBg: "#D97706", tagColor: "#fff", accent: "#B45309",
  },
};
const TRANSFER_TYPE_DEFAULT = {
  bg: "linear-gradient(135deg, #FAF5FF, #E9D5FF)",
  label: "推荐", icon: "ri-star-line",
  tagBg: "#8B5CF6", tagColor: "#fff", accent: "#7C3AED",
};

interface TransferQueryRaw {
  departure: string;
  destination: string;
  datetime: string;
  passengers: number;
  scene: string;
  /** ★ 预算 */
  budget?: string | null;
  /** ★ 品牌偏好 */
  brand?: string | null;
  /** ★ 车型偏好 */
  car_model?: string | null;
  distance_km: number;
  duration_min: number;
  duration_text: string;
  transfers: TransferPlanItem[];
}

export default function TransferCard({ data, onTransferSelect }: { data: SseTransferMessage | TransferQueryRaw; onTransferSelect?: (plan: TransferPlanItem) => void }) {
  const td = ("data" in data && (data as SseTransferMessage).data)
    ? (data as SseTransferMessage).data
    : data as TransferQueryRaw;

  if (!td?.transfers?.length) return null;

  return (
    <div className="flex flex-col gap-0 rounded-2xl w-full overflow-hidden"
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(37,99,235,0.08)",
      }}
    >
      {/* 标题栏 — 渐变背景 + 左侧装饰条 */}
      <div
        className="flex items-center gap-3 px-4 py-3.5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #C7D2FE 100%)" }}
      >
        {/* 装饰性圆形 */}
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, #818CF8 0%, transparent 70%)" }}
        />
        <div className="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 relative z-[1]"
          style={{ background: "linear-gradient(135deg, #6366F1, #4F46E5)", boxShadow: "0 2px 8px rgba(79,70,229,0.35)" }}
        >
          <i className="ri-route-line" style={{ color: "#fff", fontSize: 17 }} />
        </div>
        <div className="min-w-0 relative z-[1]">
          <div className="text-[13px] font-bold leading-tight" style={{ color: "#312E81" }}>
            {td.scene || "接送"} · {td.departure} → {td.destination}
          </div>
        </div>
      </div>

      {/* 信息条 — 紧凑两行布局 */}
      <div className="px-4 py-2"
        style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)", borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0" }}
      >
        {/* 第一行：时间 + 方案数 */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: "linear-gradient(135deg, #DBEAFE, #BFDBFE)", color: "#1D4ED8" }}
          >
            <i className="ri-time-line" style={{ fontSize: 9 }} />{td.datetime}
          </span>
          <span className="inline-flex items-center text-[9px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: "linear-gradient(135deg, #EDE9FE, #DDD6FE)", color: "#6D28D9" }}
          ><i className="ri-list-check mr-0.5" style={{ fontSize: 8 }} />共 {td.transfers.length} 套方案</span>
        </div>
        {/* 第二行：人数 | 时长 | 距离 */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px]" style={{ color: "#94A3B8" }}><i className="ri-group-line" style={{ fontSize: 9 }} /> {td.passengers}人</span>
          <span style={{ color: "#E2E8F0", fontSize: 4 }}>●</span>
          <span className="text-[9px]" style={{ color: "#94A3B8" }}>{td.duration_text}</span>
          <span style={{ color: "#E2E8F0", fontSize: 4 }}>●</span>
          <span className="text-[9px]" style={{ color: "#94A3B8" }}>{td.distance_km}km</span>
        </div>
        {/* ★ 第三行：预算 | 品牌 | 车型偏好 */}
        {(td.budget || td.brand || td.car_model) && (
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {td.budget && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                style={{ background: "#ECFDF5", color: "#059669" }}
              >
                <i className="ri-money-cny-circle-line" style={{ fontSize: 9 }} /> 预算 {td.budget}
              </span>
            )}
            {td.brand && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                style={{ background: "#FEF2F2", color: "#DC2626" }}
              >
                <i className="ri-award-line" style={{ fontSize: 9 }} /> {td.brand}
              </span>
            )}
            {td.car_model && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                style={{ background: "#F5F3FF", color: "#6D28D9" }}
              >
                <i className="ri-car-line" style={{ fontSize: 9 }} /> {td.car_model}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 方案列表 — 更紧凑 */}
      <div className="p-2.5 space-y-2.5">
        {td.transfers.map((plan: TransferPlanItem, idx: number) => {
          const pt = TRANSFER_TYPE_STYLE[plan.plan_type] || TRANSFER_TYPE_DEFAULT;
          const isPremium = plan.plan_type === "premium";

          return (
            <div key={plan.plan_name + idx}
              className={`group rounded-xl p-3 relative overflow-hidden ${onTransferSelect ? "cursor-pointer" : ""}`}
              style={{
                background: pt.bg,
                border: `1.5px solid ${pt.accent}20`,
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: isPremium
                  ? "0 2px 12px rgba(217,119,6,0.12), 0 1px 3px rgba(0,0,0,0.04)"
                  : "0 1px 6px rgba(59,130,246,0.08), 0 1px 2px rgba(0,0,0,0.03)",
              }}
              onClick={() => onTransferSelect?.(plan)}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(-1.5px)";
                (e.currentTarget as HTMLElement).style.boxShadow = isPremium
                  ? "0 8px 24px rgba(217,119,6,0.18), 0 2px 6px rgba(0,0,0,0.06)"
                  : "0 6px 20px rgba(59,130,246,0.15), 0 2px 6px rgba(0,0,0,0.05)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                (e.currentTarget as HTMLElement).style.boxShadow = isPremium
                  ? "0 2px 12px rgba(217,119,6,0.12), 0 1px 3px rgba(0,0,0,0.04)"
                  : "0 1px 6px rgba(59,130,246,0.08), 0 1px 2px rgba(0,0,0,0.03)";
              }}
            >
              {/* 卡片右上角装饰角标（仅 premium） */}
              {isPremium && (
                <div className="absolute top-0 right-0 w-14 h-14 overflow-hidden rounded-tr-xl pointer-events-none">
                  <div className="absolute top-1.5 right-[-16px] w-12 h-4 origin-bottom-right rotate-45 flex items-center justify-center"
                    style={{ background: pt.tagBg }}
                  >
                    <span className="text-[7px] font-bold text-white tracking-wider">RECOMMEND</span>
                  </div>
                </div>
              )}

              {/* 第一行：方案名 + 车型座位 */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${pt.accent}18, ${pt.accent}08)` }}
                  >
                    <i className={pt.icon} style={{ color: pt.accent, fontSize: 13 }} />
                  </div>
                  <span className="text-[12px] font-bold truncate" style={{ color: "#1E293B" }}>
                    {plan.plan_name}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-1.5">
                  <span className="text-[10px] font-semibold" style={{ color: "#334155" }}>{plan.vehicle_model}</span>
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                    style={{ background: `linear-gradient(135deg, ${pt.tagBg}, ${pt.accent})`, color: pt.tagColor }}
                  >{plan.capacity}座</span>
                </div>
              </div>

              {/* 类型标签 + 车辆类型 + 适用人群（合并为一行，节省空间） */}
              <div className="flex items-center gap-2 pl-7 mb-1.5 flex-wrap">
                <span className="text-[8px] font-bold px-2 py-0.5 rounded-full tracking-wide uppercase"
                  style={{
                    background: `linear-gradient(135deg, ${pt.tagBg}, ${pt.accent})`,
                    color: pt.tagColor,
                    letterSpacing: "0.6px",
                  }}
                >{pt.label}</span>
                <span className="text-[9px]" style={{ color: "#94A3B8" }}>
                  <i className="ri-car-line mr-0.5" style={{ fontSize: 9, color: pt.accent }} />{plan.vehicle_type}
                </span>
                <span className="text-[9px]" style={{ color: "#94A3B8" }}>
                  <i className="ri-user-heart-line mr-0.5" style={{ fontSize: 9, color: pt.accent }} />{plan.suitable_for}
                </span>
                <span className="ml-auto text-[9px] font-bold flex-shrink-0" style={{ color: pt.accent }}>
                  {plan.duration_text} · {plan.distance_km}km
                </span>
              </div>

              {/* 亮点标签 */}
              {plan.highlights?.length > 0 && (
                <div className="flex items-start gap-1 pl-7 flex-wrap">
                  {plan.highlights.slice(0, 4).map((h, i) => (
                    <span key={i} className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap font-medium"
                      style={{
                        background: "rgba(16,185,129,0.08)",
                        color: "#047857",
                        border: "1px solid rgba(16,185,129,0.12)",
                      }}
                    >
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {h}
                    </span>
                  ))}
                  {plan.highlights.length > 4 && (
                    <span className="text-[8px] px-1" style={{ color: "#94A3B8" }}>+{plan.highlights.length - 4}</span>
                  )}
                </div>
              )}

              {/* 温馨提示 — 更紧凑内联风格 */}
              {plan.tips && (
                <div className="mt-1.5 ml-7 inline-flex items-start gap-1.5 px-2 py-1.5 rounded-lg text-[9px] leading-snug max-w-full"
                  style={{
                    background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)",
                    color: "#92400E",
                    border: "1px solid rgba(245,158,11,0.18)",
                  }}
                >
                  <i className="ri-lightbulb-flash-line flex-shrink-0 mt-px" style={{ fontSize: 10, color: "#D97706" }} />
                  <span className="line-clamp-2">{plan.tips}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
